import './EmailList.css';
import { Button, Flex, Input, Table, Tooltip } from 'antd';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { InputStatus } from 'antd/es/_util/statusUtils';
import { CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';

// TODO : Faire ce composant plus générique pour remplacer toutes les listes du genre
interface EmailListProps {
  values: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  pageSize?: number;
}

const EmailList: React.FC<EmailListProps> = ({
  values = [],
  onChange,
  placeholder = '',
  pageSize,
}) => {
  const [wipNewEmail, setWipNewEmail] = useState('');
  const [wipNewEmailStatus, setWipNewEmailStatus] =
    useState<InputStatus>('');

  const [editingEmailIndex, setEditingEmailIndex] = useState<
    number | null
  >(null);
  const [editingEmailValue, setEditingEmailValue] = useState<
    string | null
  >(null);
  const [editingEmailStatus, setEditingEmailStatus] =
    useState<InputStatus>('');

  const validateEmail = useCallback(
    (
      value: string,
      setStatus: React.Dispatch<React.SetStateAction<InputStatus>>
    ) => {
      // No empty string
      if (value.trim() === '') {
        setStatus('error');
        return false;
      }

      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      // Must be a valid email
      if (!regex.test(value)) {
        setStatus('error');
        return false;
      }

      // No duplicate
      if (values.includes(value)) {
        setStatus('error');
        return false;
      }

      setStatus('');
      return true;
    },
    [values]
  );

  const handleAddNewEmail = useCallback(() => {
    if (!validateEmail(wipNewEmail, setWipNewEmailStatus)) return;

    onChange(values.concat([wipNewEmail]));
    setWipNewEmail('');
  }, [values, wipNewEmail, onChange, validateEmail]);

  const handleEditEmail = useCallback(
    (index: number, value: string) => {
      if (!validateEmail(value, setEditingEmailStatus)) return;

      onChange(values.map((email, i) =>
        i === index ? value : email
      ));
      setEditingEmailIndex(null);
      setEditingEmailValue(null);
    },
    [values, onChange, validateEmail]
  );

  const toggleEditEmail = useCallback(
    (index: number, record: { key: string, label: string, value: string }) => {
      if (editingEmailIndex === index) {
        setEditingEmailIndex(null);
        setEditingEmailValue(null);
      } else {
        setEditingEmailIndex(index);
        setEditingEmailValue(record.value);
      }
    },
    [editingEmailIndex]
  );

  const handleDeleteEmail = useCallback(
    (record: { key: string, label: string, value: string }, index: number) => {
      onChange(values.filter(
        (email) => email !== record.value
      ))
      if (editingEmailIndex === index) {
        setEditingEmailIndex(null);
        setEditingEmailValue(null);
        setEditingEmailStatus('');
      }
    },
    [values, onChange]
  );

  const sortedValues = useMemo(
    () => [...values].sort(),
    [values]
  );

  return (
    <Flex vertical style={{
      border: '1px solid var(--border-color)',
      borderRadius: '6px',
      backgroundColor: 'var(--surface-color)',
    }}>
      <Flex style={{ padding: '16px', borderBottom: '1px solid var(--border-color)' }}>
        <Input
          value={wipNewEmail}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setWipNewEmail(e.target.value)}
          onPressEnter={handleAddNewEmail}
          placeholder={placeholder}
          status={wipNewEmailStatus}
        />

        <Flex
          key="-1"
          justify="end"
          align="center"
          gap="16px"
          style={{ width: '100%' }}
        >
          <Tooltip title="Ajouter" zIndex={1}>
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddNewEmail}
            />
          </Tooltip>
        </Flex>
      </Flex>
      <Table
        pagination={pageSize ? { showSizeChanger: false, pageSize: pageSize } : false}
        showHeader={false}
        dataSource={
          sortedValues.map((email) => ({
            key: email,
            label: email,
            value: email,
          }))
        }
        columns={[
          {
            title: 'Email',
            dataIndex: 'label',
            key: 'label',
            render: (_, record, index) => {
              return (
                <Flex key={index}>
                  {editingEmailIndex === index ? (
                    <Input
                      value={editingEmailValue || ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setEditingEmailValue(e.target.value)}
                      onPressEnter={() => {
                        handleEditEmail(index, editingEmailValue || '');
                      }}
                      placeholder={placeholder}
                      status={editingEmailStatus}
                    />
                  ) : (
                    <span>{record.label}</span>
                  )}
                </Flex>
              );
            },
          },
          {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (_, record, index) => {
              return (
                <Flex
                  key={index}
                  justify="end"
                  align="center"
                  gap="16px"
                  style={{ width: '100%' }}
                >
                  {
                    editingEmailIndex === index ?
                      <Tooltip title="Annuler" zIndex={1}>
                        <Button
                          icon={<CloseOutlined />}
                          onClick={() => toggleEditEmail(index, record)}
                        />
                      </Tooltip> :
                      <Tooltip title="Modifier" zIndex={1}>
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => toggleEditEmail(index, record)}
                        />
                      </Tooltip>
                  }

                  <Tooltip title="Supprimer" zIndex={1}>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteEmail(record, index)}
                    />
                  </Tooltip>
                </Flex>
              );
            },
          },
        ]}
      />
    </Flex >

  );
};

export default EmailList;
